import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaHeadset, FaPaperPlane, FaTimes } from 'react-icons/fa';
import apiClient from '../../api';
import { Button } from './Button';

// Animação de entrada suave (agora vindo de baixo para cima)
const popUp = keyframes`
  from { opacity: 0; transform: translateY(20px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

// O Container agora é apenas um wrapper de posicionamento relativo
const WidgetWrapper = styled.div`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: flex-end; // Alinha tudo à direita
`;

// Botão Redondo Flutuante (Sempre visível)
const FloatingButton = styled.button`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative; // Para garantir que fique sobre a caixa se necessário
  z-index: 2; // Fica acima da caixa

  &:hover {
    transform: scale(1.1);
    background-color: ${({ theme }) => theme.colors.secondary};
  }
`;

// Caixa de Chat (Agora posicionada absolutamente acima do botão)
const ChatBox = styled.div`
  position: absolute;
  bottom: 70px; // 60px do botão + 10px de margem
  right: 0;
  width: 320px;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 5px 25px rgba(0,0,0,0.15);
  overflow: hidden;
  animation: ${popUp} 0.3s ease-out forwards; // forwards mantém o estado final da animação
  display: flex;
  flex-direction: column;
  border: 1px solid #eee;
  z-index: 1; // Fica abaixo do botão de fechar visualmente
`;

const ChatHeader = styled.div`
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h4 {
    margin: 0;
    font-size: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  // Removemos o ícone de fechar do cabeçalho, pois o botão principal fará isso
`;

const ChatBody = styled.div`
  padding: 1rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  height: 120px;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  resize: none;
  font-family: inherit;
  font-size: 0.9rem;
  outline: none;
  margin-bottom: 0.5rem;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const CharacterCount = styled.div`
  font-size: 0.75rem;
  color: ${props => props.isNearLimit ? 'red' : '#888'};
  text-align: right;
  margin-bottom: 1rem;
`;

const SuccessMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.secondary};
  
  p { margin-bottom: 0; }
`;

const SupportWidget = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sentSuccess, setSentSuccess] = useState(false);

  const MAX_CHARS = 1000;

  const toggleOpen = () => {
      setIsOpen(!isOpen);
      if (!isOpen) {
          // Reseta estados ao abrir
          setSentSuccess(false);
          setMessage('');
      }
  };

  const handleSubmit = async () => {
    if (!message.trim()) return;

    setIsSending(true);
    try {
      await apiClient.post('/api/support', {
        customer_id: user.cliente_id,
        message: message
      });
      
      setSentSuccess(true);
      setMessage('');
      
      setTimeout(() => {
        setIsOpen(false);
        setSentSuccess(false);
      }, 3000);

    } catch (error) {
      alert("Erro ao enviar mensagem. Tente novamente ou envie um email direto.");
      console.error(error);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <WidgetWrapper>
      {isOpen && (
        <ChatBox>
          <ChatHeader>
            <h4><FaHeadset /> Suporte Rápido</h4>
            {/* Ícone de fechar removido daqui */}
          </ChatHeader>
          
          {sentSuccess ? (
            <SuccessMessage>
              <h4>Mensagem Enviada!</h4>
              <p>Entraremos em contato em breve.</p>
            </SuccessMessage>
          ) : (
            <ChatBody>
              <p style={{fontSize: '0.9rem', color: '#666', marginBottom: '1rem'}}>
                Precisa de ajuda? Descreva seu problema abaixo.
              </p>
              
              <TextArea 
                placeholder="Ex: Não consigo sincronizar minha conta..." 
                value={message}
                maxLength={MAX_CHARS}
                onChange={(e) => setMessage(e.target.value)}
                disabled={isSending}
                autoFocus // Foca automaticamente ao abrir
              />
              
              <CharacterCount isNearLimit={message.length > 900}>
                {message.length} / {MAX_CHARS}
              </CharacterCount>

              <Button 
                onClick={handleSubmit} 
                disabled={isSending || message.length === 0}
                style={{width: '100%', display: 'flex', justifyContent: 'center', gap: '0.5rem'}}
              >
                {isSending ? 'Enviando...' : <><FaPaperPlane /> Enviar Solicitação</>}
              </Button>
            </ChatBody>
          )}
        </ChatBox>
      )}

      <FloatingButton onClick={toggleOpen} title={isOpen ? "Fechar Suporte" : "Abrir Suporte"}>
        {isOpen ? <FaTimes /> : <FaHeadset />}
      </FloatingButton>
    </WidgetWrapper>
  );
};

export default SupportWidget;